import React from 'react';
import { Container, Card, TabContent, TabPane, Label, Spinner } from 'reactstrap';
import { useFormik } from 'formik';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { LOCATIONS } from 'constants/index';
import Breadcrumb from 'components/App/Breadcrumb';
import Meta from 'components/App/Meta';
import AppInputValidation from 'components/App/InputValidation';
import AppSelectValidation from 'components/App/SelectValidation';
import AppFlatPickInput from 'components/App/FlatPickInput';
import SearchIcon from 'assets/images/search-icon.svg';
import Pagination from 'components/Pagination';
import { getGlobalInvoices } from 'store/actions';
import marketApi from 'api/marketApi';
import styles from './ManagerPayment.module.scss';

const breadcrumbList = [
  {
    name: 'Quản lý thanh toán',
    isActive: true,
    link: '',
  },
];

const ITEMS_PER_PAGE = 20;

function ManagerPayment() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const { globalInvoices, globalInvoicesLoading } = useSelector((state) => state.invoice);

  const queryParams = React.useMemo(() => new URLSearchParams(location.search), [location.search]);
  const urlTab = queryParams.get('tab') || 'ALL';
  const urlPage = parseInt(queryParams.get('page'), 10) || 1;

  const [page, setPage] = React.useState(urlPage);
  const [marketOptions, setMarketOptions] = React.useState([]);
  const [filterValues, setFilterValues] = React.useState({
    q: '',
    paidFrom: '',
    paidTo: '',
    market: '',
    status: urlTab,
  });

  React.useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const response = await marketApi.getMarkets({ page: 1, limit: 20 });
        const list = response?.payload?.data || response?.data || response?.payload || [];
        if (Array.isArray(list)) {
          const options = list.map((item) => ({
            label: item.name || item.nameEN || 'Thị trường',
            value: item._id || item.id || item.name,
          }));
          setMarketOptions(options);
        }
      } catch (error) {
        setMarketOptions([]);
      }
    };
    fetchMarkets();
  }, []);

  React.useEffect(() => {
    setFilterValues((prev) => ({
      ...prev,
      status: urlTab,
    }));
  }, [urlTab]);

  React.useEffect(() => {
    setPage(urlPage);
  }, [urlPage]);

  React.useEffect(() => {
    const payload = {
      page,
      limit: ITEMS_PER_PAGE,
    };
    if (filterValues.q) {
      payload.q = filterValues.q;
    }
    if (filterValues.status === 'PENDING') {
      payload.isPaid = false;
    } else if (filterValues.status === 'PAID') {
      payload.isPaid = true;
    }
    if (filterValues.market) {
      payload.market = filterValues.market;
    }
    if (filterValues.paidFrom) {
      payload.paidFrom = filterValues.paidFrom;
    }
    if (filterValues.paidTo) {
      payload.paidTo = filterValues.paidTo;
    }

    dispatch(getGlobalInvoices(payload));
  }, [dispatch, page, filterValues]);

  const validation = useFormik({
    initialValues: {
      q: '',
      paidFrom: '',
      paidTo: '',
      market: '',
    },
    onSubmit: (values) => {
      setFilterValues((prev) => ({
        ...prev,
        ...values,
      }));
      queryParams.set('page', '1');
      history.push({ search: queryParams.toString() });
    },
  });

  const onChangeFilterOption = React.useCallback((e) => {
    validation.setFieldValue(e.name, e.value, true);
  }, [validation]);

  const onResetForm = React.useCallback(() => {
    validation.resetForm();
    setFilterValues({
      q: '',
      paidFrom: '',
      paidTo: '',
      market: '',
      status: 'ALL',
    });
    queryParams.set('tab', 'ALL');
    queryParams.set('page', '1');
    history.push({ search: queryParams.toString() });
  }, [validation, history, queryParams]);

  const onTabChange = (status) => {
    setFilterValues((prev) => ({
      ...prev,
      status,
    }));
    queryParams.set('tab', status);
    queryParams.set('page', '1');
    history.push({ search: queryParams.toString() });
  };

  const getDateRangeDisplayValue = (paidFrom, paidTo) => {
    if (paidFrom && paidTo) {
      return `${moment(paidFrom).format('DD/MM/YYYY')} đến ${moment(paidTo).format('DD/MM/YYYY')}`;
    }
    if (paidFrom) {
      return moment(paidFrom).format('DD/MM/YYYY');
    }
    return '';
  };

  const invoiceItems = React.useMemo(() => {
    if (!globalInvoices || !Array.isArray(globalInvoices.data)) return [];
    return globalInvoices.data.map((item) => {
      let placeCode = '-';
      if (item.place?.placeId) {
        placeCode = `#${item.place.placeId}`;
      } else if (item.place?._id) {
        placeCode = `#${item.place._id.slice(-8)}`;
      }

      return {
        id: item._id,
        placeCode,
        placeName: item.place?.name || 'Chưa có thông tin',
        market: item.place?.market?.name || item.place?.market?.nameEN || '-',
        bankName: item.place?.bankAccount?.bankName || '-',
        bankAccount: item.place?.bankAccount?.accountNumber || '-',
        accountHolder: item.place?.bankAccount?.accountName || '-',
        invoiceCode: item.invoiceCode ? `#${item.invoiceCode}` : '-',
        amount: item.totalAmount ? `VND ${item.totalAmount.toLocaleString('vi-VN')}` : 'VND 0',
        status: item.isPaid ? 'PAID' : 'PENDING',
        paymentDate: item.paidAt ? moment(item.paidAt).format('DD/MM/YYYY') : '-',
      };
    });
  }, [globalInvoices]);

  const totalCount = globalInvoices?.total ?? globalInvoices?.totalItems ?? invoiceItems.length;
  const pageCount = Math.ceil(totalCount / ITEMS_PER_PAGE) || 1;

  const onChangePage = React.useCallback(
    (selected) => {
      queryParams.set('page', (selected.selected + 1).toString());
      history.push({ search: queryParams.toString() });
    },
    [history, queryParams],
  );

  const renderTableContent = () => {
    if (globalInvoicesLoading) {
      return (
        <tr>
          <td colSpan="7" className="text-center p-4">
            <Spinner size="sm" color="primary" />
            {' '}
            Đang tải dữ liệu...
          </td>
        </tr>
      );
    }

    if (invoiceItems.length === 0) {
      return (
        <tr>
          <td colSpan="7" className="text-center p-4 text-muted">
            Không tìm thấy dữ liệu thanh toán phù hợp
          </td>
        </tr>
      );
    }

    return invoiceItems.map((item) => (
      <tr key={item.id}>
        <td className={styles.ColPlace}>
          <div className={styles.PlaceCode}>{item.placeCode}</div>
          <div className={styles.PlaceName}>{item.placeName}</div>
          <div className={styles.PlaceMarket}>{item.market}</div>
        </td>
        <td className={styles.ColBank}>
          <div className={styles.BankName}>{item.bankName}</div>
          <div className={styles.BankAccount}>{item.bankAccount}</div>
          <div className={styles.AccountHolder}>{item.accountHolder}</div>
        </td>
        <td className={styles.ColInvoice}>
          <span className={styles.InvoiceCode}>{item.invoiceCode}</span>
        </td>
        <td className={styles.ColAmount}>
          <span className={styles.Amount}>{item.amount}</span>
        </td>
        <td className={styles.ColStatus}>
          <span
            className={`${styles.StatusBadge} ${
              item.status === 'PENDING' ? styles.Pending : styles.Paid
            }`}
          >
            {item.status === 'PENDING' ? 'Chờ thanh toán' : 'Đã thanh toán'}
          </span>
        </td>
        <td className={styles.ColDate}>{item.paymentDate}</td>
        <td className={styles.ColAction}>
          <Link to={`${LOCATIONS.MANAGER_PAYMENT}/${item.id}`} className={styles.ActionLink}>Xem chi tiết</Link>
        </td>
      </tr>
    ));
  };

  return (
    <>
      <div className={`page-content ${styles.Page}`}>
        <Meta name="Quản lý thanh toán" />
        <Container fluid>
          <Breadcrumb
            pageTitle="Quản lý thanh toán"
            breadcrumbList={breadcrumbList}
          />

          <Card className={styles.ContentForm}>
            <form onSubmit={validation.handleSubmit}>
              <div className={styles.FormWrapper}>
                <div className={styles.SearchGroup}>
                  <div className={styles.FieldLabel}>Tìm kiếm</div>
                  <div className={styles.InputWrapper}>
                    <img src={SearchIcon} alt="search" className={styles.SearchIcon} />
                    <AppInputValidation
                      name="q"
                      id="q"
                      type="text"
                      placeholder="Tìm theo Mã hoá đơn..."
                      validation={validation}
                      inputClassName={styles.SearchInput}
                    />
                  </div>
                </div>

                <div className={styles.FilterGroup}>
                  <div className={styles.FieldLabel}>Bộ lọc</div>
                  <div className={styles.FilterInputsRow}>
                    <AppFlatPickInput
                      name="paymentDateRange"
                      placeholder="Từ ngày - Đến ngày"
                      validation={validation}
                      className={styles.CommonFlatpickr}
                      value={getDateRangeDisplayValue(
                        validation.values.paidFrom,
                        validation.values.paidTo,
                      )}
                      options={{
                        mode: 'range',
                        showMonths: 2,
                        dateFormat: 'd/m/Y',
                      }}
                      onChange={(selectedDates) => {
                        if (selectedDates && selectedDates.length === 2) {
                          const fromStr = moment(selectedDates[0]).format('YYYY-MM-DD');
                          const toStr = moment(selectedDates[1]).format('YYYY-MM-DD');
                          validation.setFieldValue('paidFrom', fromStr, true);
                          validation.setFieldValue('paidTo', toStr, true);
                        } else if (selectedDates && selectedDates.length === 1) {
                          const fromStr = moment(selectedDates[0]).format('YYYY-MM-DD');
                          validation.setFieldValue('paidFrom', fromStr, true);
                          validation.setFieldValue('paidTo', '', true);
                        } else {
                          validation.setFieldValue('paidFrom', '', true);
                          validation.setFieldValue('paidTo', '', true);
                        }
                      }}
                    />
                    <AppSelectValidation
                      placeholder="Thị trường"
                      name="market"
                      options={marketOptions}
                      validation={validation}
                      value={validation.values.market}
                      onChange={onChangeFilterOption}
                      isHiddenSearchable
                      isClearable
                      selectedClassName={styles.CommonSelect}
                    />
                  </div>
                </div>

                <div className={styles.ButtonGroup}>
                  <button
                    type="button"
                    className={styles.BtnReset}
                    onClick={onResetForm}
                  >
                    Nhập lại
                  </button>
                  <button
                    type="submit"
                    className={styles.BtnSubmit}
                  >
                    Tìm kiếm
                  </button>
                </div>
              </div>
            </form>
          </Card>

          <Card className={styles.Content}>
            <div className="Tab__Header">
              <div className="Tab__Header__Scroll">
                <div className="Tab__Header__Tab">
                  <button
                    type="button"
                    onClick={() => onTabChange('ALL')}
                    className={`Tab__Header__Tab__Item ${filterValues.status === 'ALL' && 'Active'}`}
                  >
                    Tất cả
                  </button>
                </div>
                <div className="Tab__Header__Tab">
                  <button
                    type="button"
                    onClick={() => onTabChange('PENDING')}
                    className={`Tab__Header__Tab__Item ${filterValues.status === 'PENDING' && 'Active'}`}
                  >
                    Chờ thanh toán
                  </button>
                </div>
                <div className="Tab__Header__Tab">
                  <button
                    type="button"
                    onClick={() => onTabChange('PAID')}
                    className={`Tab__Header__Tab__Item ${filterValues.status === 'PAID' && 'Active'}`}
                  >
                    Đã thanh toán
                  </button>
                </div>
              </div>
            </div>

            <TabContent
              activeTab={filterValues.status || 'ALL'}
              className="Table__Body"
            >
              <Label className="Table__Body__Label">
                {`${totalCount} yêu cầu thanh toán`}
              </Label>

              {['ALL', 'PENDING', 'PAID'].map((status) => (
                <TabPane
                  tabId={status}
                  key={status}
                >
                  <div className={styles.TableContainer}>
                    <table className={styles.Table}>
                      <thead>
                        <tr>
                          <th className={styles.ColPlace}>Mã chỗ nghỉ | Tên | Thị trường</th>
                          <th className={styles.ColBank}>Thông tin chuyển khoản</th>
                          <th className={styles.ColInvoice}>Mã hoá đơn</th>
                          <th className={styles.ColAmount}>Tổng số tiền</th>
                          <th className={styles.ColStatus}>Trạng thái</th>
                          <th className={styles.ColDate}>Ngày thanh toán</th>
                          <th className={styles.ColAction}>Thao tác</th>
                        </tr>
                      </thead>
                      <tbody>
                        {renderTableContent()}
                      </tbody>
                    </table>
                  </div>
                </TabPane>
              ))}
            </TabContent>
          </Card>

          {invoiceItems.length !== 0 && (
            <Pagination
              page={page}
              pageCount={pageCount}
              onChangePage={onChangePage}
            />
          )}
        </Container>
      </div>
    </>
  );
}

export default ManagerPayment;
